import { APIEvents } from '../enums/api-events';
import { sendCustomEvent } from './send-custom-event';

export type AuthChannelMessage = {
  type: APIEvents.LOGIN | APIEvents.LOGOUT;
};

export class AuthMessageController {
  static #listenChannel: BroadcastChannel;
  static #sendChannel: BroadcastChannel;
  static #abortController: AbortController | undefined;

  private static get isBrowser() {
    return typeof window !== 'undefined';
  }

  private static get supportsBroadcastChannel() {
    return typeof BroadcastChannel !== 'undefined';
  }

  private static get preferBroadcast() {
    return this.isBrowser && this.supportsBroadcastChannel;
  }

  private static get listenChannel(): BroadcastChannel | null {
    if (!AuthMessageController.#listenChannel) {
      AuthMessageController.#listenChannel = new BroadcastChannel('auth-channel');
    }

    return AuthMessageController.#listenChannel;
  }

  private static get sendChannel(): BroadcastChannel | null {
    if (!AuthMessageController.#sendChannel) {
      AuthMessageController.#sendChannel = new BroadcastChannel('auth-channel');
    }

    return AuthMessageController.#sendChannel;
  }

  /**
   * Register to message events and sends a custom event.
   */
  public static sendEventOnMessage() {
    if (!this.preferBroadcast) {
      console.warn('BroadcastChannel is not being used, so multi-tab login/logout will not work');
      return;
    }

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    this.listenChannel.addEventListener(
      'message',
      (event: MessageEvent<AuthChannelMessage>) => {
        if (event.data.type === APIEvents.LOGIN) {
          sendCustomEvent(APIEvents.LOGIN);
        } else if (event.data.type === APIEvents.LOGOUT) {
          sendCustomEvent(APIEvents.LOGOUT);
        }
      },
      { signal: this.#abortController.signal },
    );
  }

  /**
   * Sends a message to the broadcast channel if is supported by the browser
   * @param message - Message to be sent
   */
  public static send(message: APIEvents.LOGIN | APIEvents.LOGOUT) {
    if (!this.preferBroadcast) {
      sendCustomEvent(message);
      return;
    }
    this.sendChannel.postMessage({ type: message });
    return;
  }
}
